import type { ReactNode } from "react";
import type { ProductCardResponse } from "@/shared/api/generated/iSHONCHPlatformAPI.schemas";
import { cn } from "@/shared/lib/cn";
import { ProductCard } from "./ProductCard";

/**
 * 2 columns with 8 px gaps on phones (white tiles on the grey canvas, D12/D13), up to `columns` on desktop
 * (Figma: 5 on the home page, 4 in listings).
 */
export function ProductGrid({
  products,
  columns = 4,
  renderActions,
}: {
  products: ProductCardResponse[];
  columns?: 4 | 5 | 3;
  /** Extra buttons on each card (e.g. the favorites heart). */
  renderActions?: (product: ProductCardResponse) => ReactNode;
}) {
  return (
    <ul
      className={cn(
        "grid grid-cols-2 gap-2 sm:grid-cols-3 lg:gap-x-4 lg:gap-y-8",
        COLUMNS[columns],
      )}
    >
      {products.map((product) => (
        <li key={product.p_id}>
          <ProductCard product={product} actions={renderActions?.(product)} />
        </li>
      ))}
    </ul>
  );
}

const COLUMNS = {
  5: "lg:grid-cols-4 xl:grid-cols-5",
  4: "lg:grid-cols-3 xl:grid-cols-4",
  3: "lg:grid-cols-3",
} as const;

/**
 * A horizontal row of cards on phones (D19: 148 px cards, 2.4 visible, snapping), the regular grid on desktop.
 */
export function ProductRail({
  products,
  columns = 5,
  renderActions,
  label,
}: {
  products: ProductCardResponse[];
  columns?: 4 | 5;
  renderActions?: (product: ProductCardResponse) => ReactNode;
  label: string;
}) {
  return (
    <ul
      aria-label={label}
      className={cn(
        "-mx-3 no-scrollbar flex snap-x snap-mandatory scroll-px-3 gap-2 overflow-x-auto overscroll-x-contain px-3 sm:-mx-4 sm:scroll-px-4 sm:px-4",
        "lg:mx-0 lg:grid lg:snap-none lg:gap-x-4 lg:gap-y-8 lg:overflow-visible lg:px-0",
        COLUMNS[columns],
      )}
    >
      {products.map((product) => (
        <li key={product.p_id} className="w-37 shrink-0 snap-start lg:w-auto">
          <ProductCard product={product} actions={renderActions?.(product)} />
        </li>
      ))}
    </ul>
  );
}

/** Grey placeholders with the grid's shape, so the page does not jump when data arrives. */
export function ProductGridSkeleton({ count = 8 }: { count?: number }) {
  return (
    <ul
      className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4 lg:gap-x-4 lg:gap-y-8"
      aria-hidden
    >
      {Array.from({ length: count }, (_, index) => (
        <li
          key={index}
          className="flex animate-pulse flex-col gap-2 rounded-card bg-surface p-2 pb-3 lg:gap-3 lg:bg-transparent lg:p-0"
        >
          <div className="aspect-square rounded-lg2 bg-canvas lg:rounded-card" />
          <div className="h-6 w-4/5 rounded-md bg-canvas" />
          <div className="h-5 w-1/2 rounded bg-canvas" />
          <div className="h-4 w-3/4 rounded bg-canvas" />
        </li>
      ))}
    </ul>
  );
}
